import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircleIcon, RefreshCwIcon, ServerIcon } from 'lucide-react'
import { useState } from 'react'
import { k8sApi, type HiveMQPod } from '~/lib/api'
import { FileTree } from './FileTree'
import { LogViewerModal } from './LogViewerModal'

interface HiveMQLogsTabProps {
  namespace: string
  isAdmin: boolean
}

function PodStatusBadge({ pod }: { pod: HiveMQPod }) {
  const colorPalette = pod.ready ? 'green' : pod.status === 'Running' ? 'yellow' : 'red'
  return (
    <Badge colorPalette={colorPalette} variant="subtle" fontSize="xs">
      {pod.status}
    </Badge>
  )
}

export function HiveMQLogsTab({ namespace: _namespace, isAdmin: _isAdmin }: HiveMQLogsTabProps) {
  const [selectedPod, setSelectedPod] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [viewingFile, setViewingFile] = useState<string | null>(null)

  // Fetch HiveMQ pods
  const {
    data: podsData,
    isLoading: podsLoading,
    error: podsError,
    refetch: refetchPods,
  } = useQuery({
    queryKey: ['hivemq-pods'],
    queryFn: () => k8sApi.hivemqPods(),
  })

  // Fetch log files for selected pod
  const {
    data: filesData,
    isLoading: filesLoading,
    error: filesError,
    refetch: refetchFiles,
  } = useQuery({
    queryKey: ['hivemq-log-files', selectedPod],
    queryFn: () => k8sApi.hivemqLogFiles(selectedPod),
    enabled: !!selectedPod,
  })

  // Auto-select first pod when pods are loaded
  if (podsData?.pods && podsData.pods.length > 0 && !selectedPod) {
    setSelectedPod(podsData.pods[0].name)
  }

  const handlePodChange = (podName: string) => {
    setSelectedPod(podName)
    setSelectedFile(null)
    setViewingFile(null)
  }

  const handleSelectFile = (path: string) => {
    setSelectedFile(path)
    setViewingFile(path)
  }

  const handleRefresh = () => {
    refetchPods()
    if (selectedPod) {
      refetchFiles()
    }
  }

  // Loading state for pods
  if (podsLoading) {
    return (
      <Flex justify="center" align="center" py={12}>
        <VStack gap={3}>
          <Spinner size="lg" />
          <Text color="content.secondary">Loading HiveMQ pods...</Text>
        </VStack>
      </Flex>
    )
  }

  // Error state for pods
  if (podsError) {
    return (
      <Flex justify="center" align="center" py={12}>
        <VStack gap={3}>
          <AlertCircleIcon size={48} color="var(--chakra-colors-red-500)" />
          <Text color="danger.fg">
            Failed to load HiveMQ pods:{' '}
            {podsError instanceof Error ? podsError.message : 'Unknown error'}
          </Text>
          <Button onClick={() => refetchPods()}>
            <RefreshCwIcon size={16} />
            Retry
          </Button>
        </VStack>
      </Flex>
    )
  }

  // No pods found
  if (!podsData?.pods || podsData.pods.length === 0) {
    return (
      <Flex justify="center" align="center" py={12}>
        <VStack gap={3}>
          <Box color="content.secondary">
            <ServerIcon size={48} />
          </Box>
          <Text color="content.secondary">No HiveMQ pods found</Text>
          <Text fontSize="sm" color="content.tertiary">
            Make sure HiveMQ Enterprise pods are running in the cluster
          </Text>
          <Button variant="outline" onClick={() => refetchPods()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        </VStack>
      </Flex>
    )
  }

  const currentPod = podsData.pods.find((p) => p.name === selectedPod)

  return (
    <VStack align="stretch" gap={4}>
      {/* Header with Pod Selector */}
      <Flex justify="space-between" align="center">
        <HStack gap={4}>
          <HStack gap={2}>
            <Text fontWeight="medium">Pod:</Text>
            <NativeSelect.Root size="sm" w="200px">
              <NativeSelect.Field
                aria-label="Select HiveMQ pod"
                value={selectedPod}
                onChange={(e) => handlePodChange(e.target.value)}
              >
                {podsData.pods.map((pod) => (
                  <option key={pod.name} value={pod.name}>
                    {pod.name}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </HStack>
          {currentPod && (
            <HStack gap={2}>
              <PodStatusBadge pod={currentPod} />
              <Text fontSize="sm" color="content.secondary">
                {currentPod.namespace}
              </Text>
            </HStack>
          )}
        </HStack>
        <Button size="sm" onClick={handleRefresh}>
          <RefreshCwIcon size={16} />
          Refresh
        </Button>
      </Flex>

      {/* File Tree Card */}
      <Card.Root>
        <Card.Header>
          <Flex justify="space-between" align="center">
            <VStack align="flex-start" gap={0}>
              <Heading size="sm">Log Files</Heading>
              <Text fontSize="sm" color="content.secondary">
                /opt/hivemq/logs
              </Text>
            </VStack>
            {filesData && <Badge variant="outline">{filesData.entries.length} items</Badge>}
          </Flex>
        </Card.Header>
        <Card.Body pt={0}>
          {filesError ? (
            <Flex justify="center" align="center" py={8}>
              <VStack gap={2}>
                <Text color="danger.fg">
                  Failed to load files:{' '}
                  {filesError instanceof Error ? filesError.message : 'Unknown error'}
                </Text>
                <Button size="sm" variant="outline" onClick={() => refetchFiles()}>
                  <RefreshCwIcon size={16} />
                  Retry
                </Button>
              </VStack>
            </Flex>
          ) : (
            <Box
              maxH="500px"
              overflow="auto"
              borderWidth="1px"
              borderColor="border"
              borderRadius="md"
              p={2}
            >
              <FileTree
                entries={filesData?.entries || []}
                selectedFile={selectedFile}
                onSelectFile={handleSelectFile}
                isLoading={filesLoading}
              />
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {/* Instructions when no file is selected */}
      {!selectedFile && !filesLoading && filesData?.entries && filesData.entries.length > 0 && (
        <Flex justify="center" py={4}>
          <Text color="content.secondary" fontSize="sm">
            Click on a log file to view its contents
          </Text>
        </Flex>
      )}

      {/* Log Viewer Modal */}
      {viewingFile && selectedPod && (
        <LogViewerModal
          podName={selectedPod}
          filePath={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </VStack>
  )
}
